import React, { useRef, useState, useEffect, useLayoutEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { gsap } from '../hooks/useGSAP';
import Footer from '../components/common/Footer';
import { Linkedin, Mail, ChevronRight, Database, GraduationCap, Lightbulb, Heart } from 'lucide-react';
import teamData from '../data/teamMembers.json';
import { ScrollTrigger } from "gsap/ScrollTrigger";

const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

// ============================================
// HERO
// ============================================
const TeamHero = () => {
   const containerRef = useRef(null);
   const line1Ref = useRef(null);
   const line2Ref = useRef(null);

   useIsomorphicLayoutEffect(() => {
      const ctx = gsap.context(() => {
         const tl = gsap.timeline({ delay: 0.2 });
         const chars1 = line1Ref.current?.querySelectorAll('.char');
         const chars2 = line2Ref.current?.querySelectorAll('.char');
         if (chars1) tl.fromTo(chars1, { y: 100, opacity: 0 }, { y: 0, opacity: 1, duration: 1, stagger: 0.03, ease: 'power3.out' });
         if (chars2) tl.fromTo(chars2, { y: 100, opacity: 0 }, { y: 0, opacity: 1, duration: 1, stagger: 0.03, ease: 'power3.out' }, "-=0.8");
      }, containerRef);
      return () => ctx.revert();
   }, []);

   const split = (text) => text.split('').map((c, i) => <span key={i} className="char inline-block">{c === ' ' ? '\u00A0' : c}</span>);

   return (
      <section ref={containerRef} className="min-h-[50vh] flex flex-col justify-center px-6 bg-[#f5f5f7]">
         <div className="max-w-[90rem] mx-auto w-full">
            <div className="mb-12">
               <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-black/10 text-xs font-mono uppercase tracking-widest text-[#86868b]">
                  <span className="w-2 h-2 bg-black rounded-full animate-pulse" />
                  Global Collective
               </div>
            </div>
            <h1 ref={line1Ref} className="text-[clamp(3.5rem,10.5vw,10rem)] leading-[0.9] font-bold tracking-tighter text-[#1d1d1f] overflow-hidden whitespace-nowrap">
               {split("TEAM &")}
            </h1>
            <h1 ref={line2Ref} className="text-[clamp(3.5rem,10.5vw,10rem)] leading-[0.9] font-bold tracking-tighter text-[#86868b] overflow-hidden whitespace-nowrap">
               {split("MOTIVATION")}
            </h1>
         </div>
      </section>
   );
};

// ============================================
// MOTIVATION SECTION (Boxed & Emphasized)
// ============================================
const MotivationSection = () => {
   const sectionRef = useRef(null);

   useIsomorphicLayoutEffect(() => {
      const ctx = gsap.context(() => {
         gsap.from('.motivation-anim', {
            y: 40, opacity: 0, duration: 0.8, stagger: 0.15, ease: 'power3.out',
            scrollTrigger: { trigger: sectionRef.current, start: 'top 80%' }
         });
      }, sectionRef);
      return () => ctx.revert();
   }, []);

   return (
      <section ref={sectionRef} className="bg-[#f5f5f7] pt-12 md:pt-16 pb-24 md:pb-32 px-6 relative">
         {/* Left sidebar indicator */}
         <div className="absolute left-6 md:left-12 top-1/2 -translate-y-1/2 z-10 hidden md:flex flex-col items-center gap-4">
            <span className="font-mono text-[10px] text-[#86868b]">01</span>
            <div className="w-[2px] h-12 bg-black rounded-full" />
         </div>

         <div className="max-w-4xl mx-auto md:pl-12">
            <div className="motivation-anim mb-12 text-center">
               <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-black/10 text-xs font-mono uppercase tracking-widest text-[#86868b] mb-6">
                  <Lightbulb size={14} />
                  Our Origin Story
               </span>
               <h2 className="text-4xl md:text-5xl font-bold tracking-tighter text-[#1d1d1f]">How It All Began</h2>
            </div>

            {/* The boxed, highly emphasized motivation */}
            <div className="motivation-anim relative">
               <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 rounded-3xl blur-sm opacity-20" />
               <div className="relative bg-white border-2 border-[#1d1d1f] rounded-3xl p-8 md:p-12 shadow-2xl">
                  <div className="absolute top-0 left-8 -translate-y-1/2">
                     <span className="bg-[#1d1d1f] text-white text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full">
                        Key Motivation
                     </span>
                  </div>

                  <div className="space-y-6 text-lg md:text-xl leading-relaxed text-[#1d1d1f]/80 font-light">
                     <p>
                        The Z-SCANNER collaboration began as a group of curious students brought together by a shared fascination with fundamental physics. <strong className="text-[#1d1d1f] font-semibold">Vedansh, Amolika, Toshani</strong> (now based in Canada), <strong className="text-[#1d1d1f] font-semibold">Prithvi, Rizul, Yash,</strong> and <strong className="text-[#1d1d1f] font-semibold">Abhimanyu</strong> first met during the <strong className="text-[#1d1d1f] font-semibold">Lodha Genius Programme at Ashoka University</strong>.
                     </p>
                     <p>
                        Our interest in particle physics grew significantly during the <strong className="text-[#1d1d1f] font-semibold">Great Ideas Seminar in the Takshila Lecture Hall</strong>, where talks by scientists such as <em>Archana Sharma, Tarun Souradeep, Brian Schmidt,</em> and <em>Somak Raychaudhury</em> demonstrated how physics pushes the boundaries.
                     </p>
                     <p>
                        Motivated by these experiences, we formed an international team to pursue hands-on research. Our group combines different strengths: theoretical curiosity, data analysis, and interdisciplinary scientific training. <strong className="text-[#1d1d1f] font-semibold">Aisun</strong> (Physics/Mathematics) and <strong className="text-[#1d1d1f] font-semibold">Enlik</strong> (Chemistry/Biology) bring rigorous STEM preparation from Nazarbayev Intellectual School. <strong className="text-[#1d1d1f] font-semibold">Tranav</strong> coordinates research audits.
                     </p>
                     <p className="text-[#1d1d1f] font-medium text-xl md:text-2xl border-l-4 border-[#1d1d1f] pl-6 mt-8">
                        This diversity of expertise allows our team to approach experimental challenges creatively, and it is the spirit we aim to bring to the beamline.
                     </p>
                  </div>
               </div>
            </div>
         </div>
      </section>
   );
};

// ============================================
// TEACHERS & MENTORS SECTION
// ============================================
const MentorsSection = () => {
   const sectionRef = useRef(null);

   useIsomorphicLayoutEffect(() => {
      const ctx = gsap.context(() => {
         gsap.from('.mentor-anim', {
            y: 30, opacity: 0, duration: 0.7, stagger: 0.12, ease: 'power3.out',
            scrollTrigger: { trigger: sectionRef.current, start: 'top 80%' }
         });
      }, sectionRef);
      return () => ctx.revert();
   }, []);

   return (
      <section ref={sectionRef} className="bg-white py-24 md:py-32 px-6 border-t border-gray-200 relative">
         {/* Left sidebar indicator */}
         <div className="absolute left-6 md:left-12 top-1/2 -translate-y-1/2 z-10 hidden md:flex flex-col items-center gap-4">
            <span className="font-mono text-[10px] text-[#86868b]">12</span>
            <div className="w-[2px] h-12 bg-black rounded-full" />
         </div>

         <div className="max-w-5xl mx-auto md:pl-12">
            <div className="mentor-anim mb-16 text-center">
               <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-black/10 text-xs font-mono uppercase tracking-widest text-[#86868b] mb-6">
                  <GraduationCap size={14} />
                  Guidance
               </span>
               <h2 className="text-4xl md:text-5xl font-bold tracking-tighter text-[#1d1d1f]">Teachers & Mentors</h2>
            </div>

            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
               {/* Teachers */}
               <div className="mentor-anim bg-[#f5f5f7] rounded-2xl p-8 border border-gray-100">
                  <div className="w-12 h-12 bg-[#1d1d1f] rounded-xl flex items-center justify-center mb-6">
                     <GraduationCap size={24} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#1d1d1f] mb-3">Physics Teachers</h3>
                  <p className="text-[#1d1d1f]/70 leading-relaxed">
                     We sincerely thank our physics teachers <strong className="text-[#1d1d1f]">Mr. Abhishek Choudhary</strong> and <strong className="text-[#1d1d1f]">Ms. Raimbayeva Zhanat Saparbekovna</strong> for their foundational guidance.
                  </p>
               </div>

               <div className="mentor-anim bg-[#f5f5f7] rounded-2xl p-8 border border-gray-100">
                  <div className="w-12 h-12 bg-[#1d1d1f] rounded-xl flex items-center justify-center mb-6">
                     <Database size={24} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#1d1d1f] mb-3">Computer Science Teachers</h3>
                  <p className="text-[#1d1d1f]/70 leading-relaxed">
                     We are grateful to our Computer Science teachers, <strong className="text-[#1d1d1f]">Ms. Vishesha Sharma</strong> and <strong className="text-[#1d1d1f]">Mr. Anurag Sinha</strong>, for their support in data analysis.
                  </p>
               </div>

               <div className="mentor-anim bg-[#f5f5f7] rounded-2xl p-8 border border-gray-100 md:col-span-2 lg:col-span-1">
                  <div className="w-12 h-12 bg-[#1d1d1f] rounded-xl flex items-center justify-center mb-6">
                     <Lightbulb size={24} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#1d1d1f] mb-3">Research Mentor</h3>
                  <p className="text-[#1d1d1f]/70 leading-relaxed">
                     We started working on the proposal from early October. <strong className="text-[#1d1d1f]">Sayan Chakraborty</strong> and <strong className="text-[#1d1d1f]">Subir Mandal</strong> (currently pursuing his PhD at Bose Institute) helped us stress-test our proposal and acted as mentor. Thanks to their advisory. We were referred to him by <strong className="text-[#1d1d1f]">Prof. Saikat Biswas</strong> (Bose Institute).
                  </p>
               </div>
            </div>
         </div>
      </section>
   );
};

// ============================================
// ACKNOWLEDGMENTS SECTION
// ============================================
const AcknowledgmentsSection = () => {
   const sectionRef = useRef(null);

   useIsomorphicLayoutEffect(() => {
      const ctx = gsap.context(() => {
         gsap.from('.ack-anim', {
            y: 30, opacity: 0, duration: 0.7, stagger: 0.12, ease: 'power3.out',
            scrollTrigger: { trigger: sectionRef.current, start: 'top 80%' }
         });
      }, sectionRef);
      return () => ctx.revert();
   }, []);

   return (
      <section ref={sectionRef} className="bg-[#f5f5f7] py-24 md:py-32 px-6 border-t border-gray-200 relative">
         {/* Left sidebar indicator */}
         <div className="absolute left-6 md:left-12 top-1/2 -translate-y-1/2 z-10 hidden md:flex flex-col items-center gap-4">
            <span className="font-mono text-[10px] text-[#86868b]">13</span>
            <div className="w-[2px] h-12 bg-black rounded-full" />
         </div>

         <div className="max-w-4xl mx-auto md:pl-12">
            <div className="ack-anim mb-12 text-center">
               <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-black/10 text-xs font-mono uppercase tracking-widest text-[#86868b] mb-6">
                  <Heart size={14} />
                  Gratitude
               </span>
               <h2 className="text-4xl md:text-5xl font-bold tracking-tighter text-[#1d1d1f]">Acknowledgments</h2>
            </div>

            <div className="space-y-8">
               <div className="ack-anim bg-white rounded-2xl p-8 md:p-10 border border-gray-100 shadow-sm">
                  <h3 className="text-lg font-bold text-[#1d1d1f] mb-4 uppercase tracking-wider text-sm">Acknowledgments</h3>
                  <p className="text-[#1d1d1f]/70 leading-relaxed text-lg">
                     Thanks to <strong className="text-[#1d1d1f]">Prof. Saikat Biswas</strong> too for referring us to him. We thank <strong className="text-[#1d1d1f]">Prof. Igor Ivanov</strong>, <strong className="text-[#1d1d1f]">Prof. Grigory Feofilov</strong>, <strong className="text-[#1d1d1f]">Prof. Christian Binek</strong>, <strong className="text-[#1d1d1f]">Dr. Volodymyr M. Shapoval</strong>, <strong className="text-[#1d1d1f]">Prof. Graziano Venanzoni</strong>, <strong className="text-[#1d1d1f]">Dr. Lorenzo Cotrozzi</strong>, <strong className="text-[#1d1d1f]">Prof. Jaap Velthuis</strong>, <strong className="text-[#1d1d1f]">Dr. Thorsten Lux</strong>, <strong className="text-[#1d1d1f]">Prof. Andreas Warburton</strong>, <strong className="text-[#1d1d1f]">Prof. Eric Maluta</strong>, <strong className="text-[#1d1d1f]">Prof. Mark Vagins</strong>, <strong className="text-[#1d1d1f]">Prof. Francesco Forti</strong>, <strong className="text-[#1d1d1f]">Dr. Andrea Dainese</strong>, <strong className="text-[#1d1d1f]">Prof. Osamu Jinnouchi</strong>, <strong className="text-[#1d1d1f]">Dr. Yanlin Liu</strong>, <strong className="text-[#1d1d1f]">Prof. Jesse Thaler</strong>, <strong className="text-[#1d1d1f]">Dr. Arka Santra</strong>, <strong className="text-[#1d1d1f]">Prof. Takashi Nakano</strong>, and <strong className="text-[#1d1d1f]">Mr. Rajesh Singh</strong> for their helpful discussions, comments, advice, and suggestions for this work.
                  </p>
               </div>

               <div className="ack-anim bg-white rounded-2xl p-8 md:p-10 border border-gray-100 shadow-sm">
                  <h3 className="text-lg font-bold text-[#1d1d1f] mb-4 uppercase tracking-wider text-sm">Clinical Applications</h3>
                  <p className="text-[#1d1d1f]/70 leading-relaxed text-lg">
                     Thanks to <strong className="text-[#1d1d1f]">Dr. Helge Egil Seime Pettersen</strong> and <strong className="text-[#1d1d1f]">Dr. Nicolas Depauw</strong> for their expert perspectives on clinical applications.
                  </p>
               </div>

               <div className="ack-anim bg-white rounded-2xl p-8 md:p-10 border border-gray-100 shadow-sm">
                  <h3 className="text-lg font-bold text-[#1d1d1f] mb-4 uppercase tracking-wider text-sm">Institutional Support</h3>
                  <p className="text-[#1d1d1f]/70 leading-relaxed text-lg">
                     We also thank <strong className="text-[#1d1d1f]">Ashoka University</strong> for access to research databases and <strong className="text-[#1d1d1f]">HDFC Library</strong> for providing e-books.
                  </p>
               </div>
            </div>
         </div>
      </section>
   );
};

// ============================================
// EMAIL HOVER REVEAL COMPONENT
// ============================================
const EmailReveal = ({ email }) => {
   const [showEmail, setShowEmail] = useState(false);
   const [fading, setFading] = useState(false);
   const hoverStartRef = useRef(null);
   const fadeTimerRef = useRef(null);
   const hideTimerRef = useRef(null);

   const clearAllTimers = () => {
      if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
   };

   const handleMouseEnter = () => {
      clearAllTimers();
      hoverStartRef.current = Date.now();
      setFading(false);
      setShowEmail(true);
   };

   const handleMouseLeave = () => {
      const hoverDuration = Date.now() - (hoverStartRef.current || 0);

      // Quick hover (< 300ms) = scroll-through, hide immediately
      if (hoverDuration < 300) {
         setShowEmail(false);
         setFading(false);
         return;
      }

      // Intentional hover: stay visible 2.4s, then fade 0.6s, then hide
      setFading(false);
      fadeTimerRef.current = setTimeout(() => {
         setFading(true);
         hideTimerRef.current = setTimeout(() => {
            setShowEmail(false);
            setFading(false);
         }, 600); // matches CSS transition duration
      }, 2400);
   };

   // Cleanup timers on unmount
   useEffect(() => {
      return () => clearAllTimers();
   }, []);

   return (
      <div className="relative flex items-center gap-3">
         <button
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={() => window.location.href = `mailto:${email}`}
            className="flex items-center justify-center w-12 h-12 bg-white border border-gray-200 shadow-sm rounded-full hover:bg-black hover:text-white hover:scale-110 transition-all duration-300 cursor-pointer"
         >
            <Mail size={20} className="text-current transition-colors" />
         </button>
         {showEmail && (
            <a
               href={`mailto:${email}`}
               className="absolute left-14 whitespace-nowrap px-4 py-2 bg-white border border-gray-200 rounded-full text-sm font-mono text-[#1d1d1f] shadow-lg hover:bg-black hover:text-white transition-all duration-300"
               style={{
                  opacity: fading ? 0 : 1,
                  transition: 'opacity 0.6s ease',
               }}
            >
               {email}
            </a>
         )}
      </div>
   );
};

// ============================================
// PREMIUM CONTINUOUS SCROLL ROSTER
// ============================================
const ScrollRoster = () => {
   const containerRef = useRef(null);
   const leftSidesRef = useRef([]);
   const rightSidesRef = useRef([]);
   const progressBarsRef = useRef([]);

   const members = (teamData.team || []).map((m, i) => ({
      ...m,
      id: `OP-${100 + i}`,
      index: i + 1,
      total: teamData.team?.length || 0
   }));

   useEffect(() => {
      // Wait for Home's WebGL context teardown and for refs to be fully attached
      const timer = setTimeout(() => {

         const allRefsReady = members.every((_, i) =>
            leftSidesRef.current[i] && rightSidesRef.current[i]
         );
         if (!allRefsReady) return;

         let ctx = gsap.context(() => {

            const tl = gsap.timeline({
               scrollTrigger: {
                  trigger: containerRef.current,
                  start: "top top",
                  end: `+=${members.length * 150}%`,
                  pin: true,
                  scrub: 1,
               }
            });

            members.forEach((_, i) => {
               const leftEl = leftSidesRef.current[i];
               const rightEl = rightSidesRef.current[i];
               if (!leftEl || !rightEl) return;

               const leftTextBlocks = leftEl.querySelectorAll('.anim-text');
               const leftTags = leftEl.querySelectorAll('.anim-tag');
               const leftSocial = leftEl.querySelector('.anim-social');
               const rightContainer = rightEl.querySelector('.anim-img-container');
               const rightImg = rightEl.querySelector('.anim-img');
               const rightName = rightEl.querySelector('.anim-right-name');

               gsap.set([leftEl, rightEl, rightContainer, rightImg, ...leftTextBlocks, ...leftTags], { force3D: true });
               gsap.set(rightEl, { zIndex: i });
               gsap.set(leftEl, { zIndex: i });

               if (i === 0) {
                  gsap.set(leftEl, { autoAlpha: 1 });
                  gsap.set(rightEl, { autoAlpha: 1 });
                  gsap.set(leftTextBlocks, { y: 0, autoAlpha: 1 });
                  gsap.set(leftTags, { y: 0, autoAlpha: 1, scale: 1 });
                  if (leftSocial) gsap.set(leftSocial, { y: 0, autoAlpha: 1 });
                  gsap.set(rightContainer, { clipPath: "inset(0% 0% 0% 0% round 32px)", scale: 1 });
                  gsap.set(rightImg, { scale: 1, autoAlpha: 1 });
                  if (rightName) gsap.set(rightName, { y: 0, autoAlpha: 1 });
               } else {
                  gsap.set(leftEl, { autoAlpha: 0 });
                  gsap.set(rightEl, { autoAlpha: 1 });
                  gsap.set(rightContainer, { clipPath: "inset(100% 0% 0% 0% round 32px)", scale: 1.05 });
                  gsap.set(rightImg, { scale: 1.2, autoAlpha: 1 });
                  gsap.set(leftTextBlocks, { y: 40, autoAlpha: 0 });
                  gsap.set(leftTags, { y: 20, autoAlpha: 0, scale: 0.9 });
                  if (leftSocial) gsap.set(leftSocial, { y: 20, autoAlpha: 0 });
                  if (rightName) gsap.set(rightName, { y: 40, autoAlpha: 0 });
               }
            });

            members.forEach((_, i) => {
               if (i === 0) return;

               const prevLeft = leftSidesRef.current[i - 1];
               const prevRight = rightSidesRef.current[i - 1];
               const currLeft = leftSidesRef.current[i];
               const currRight = rightSidesRef.current[i];
               const prevPBar = progressBarsRef.current[i - 1];
               const currPBar = progressBarsRef.current[i];

               const enterTl = gsap.timeline();

               if (prevLeft) enterTl.to(prevLeft, { autoAlpha: 0, y: -30, duration: 0.6, ease: "power2.inOut" }, 0);
               if (prevPBar) enterTl.to(prevPBar, { height: "0%", autoAlpha: 0.2, duration: 0.4 }, 0);

               if (i > 1) {
                  const olderRight = rightSidesRef.current[i - 2];
                  if (olderRight) enterTl.set(olderRight, { autoAlpha: 0 }, 0);
               }

               if (prevRight) {
                  const pContainer = prevRight.querySelector('.anim-img-container');
                  enterTl.to(pContainer, { scale: 0.92, opacity: 0, duration: 0.8, ease: "power2.inOut" }, 0);
               }

               if (currRight) {
                  const cContainer = currRight.querySelector('.anim-img-container');
                  const cImg = currRight.querySelector('.anim-img');
                  const cName = currRight.querySelector('.anim-right-name');

                  enterTl.to(cContainer, { clipPath: "inset(0% 0% 0% 0% round 32px)", scale: 1, duration: 0.8, ease: "power2.inOut" }, 0.1);
                  enterTl.to(cImg, { scale: 1, duration: 1.0, ease: "power3.out" }, 0.1);
                  if (cName) enterTl.to(cName, { y: 0, autoAlpha: 1, duration: 0.6, ease: "back.out(1.5)" }, 0.4);
               }

               if (currLeft) {
                  const leftTextBlocks = currLeft.querySelectorAll('.anim-text');
                  const leftTags = currLeft.querySelectorAll('.anim-tag');
                  const leftSocial = currLeft.querySelector('.anim-social');

                  enterTl.set(currLeft, { autoAlpha: 1 }, 0.2);
                  enterTl.to(leftTextBlocks, { y: 0, autoAlpha: 1, duration: 0.6, stagger: 0.05, ease: "power3.out" }, 0.3);
                  enterTl.to(leftTags, { y: 0, autoAlpha: 1, scale: 1, duration: 0.5, stagger: 0.05, ease: "back.out(1.5)" }, 0.5);
                  if (leftSocial) enterTl.to(leftSocial, { y: 0, autoAlpha: 1, duration: 0.4, ease: "power2.out" }, 0.6);
               }

               if (currPBar) enterTl.to(currPBar, { height: "100%", autoAlpha: 1, duration: 0.4 }, 0.3);

               tl.add(enterTl, "-=0.2");
            });

            ScrollTrigger.refresh();

         }, containerRef.current); // Pass the DOM node directly, not the ref object

         return () => ctx.revert();

      }, 300); // 300ms gives Home's WebGL teardown enough time to finish

      return () => clearTimeout(timer);
   }, []); // Empty deps — members is stable JSON data, no need to re-run

   return (
      <section ref={containerRef} className="bg-[#f5f5f7] h-screen w-full relative overflow-hidden flex items-center">
         <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#e5e5e8]/30 to-transparent opacity-50 pointer-events-none" />

         <div className="absolute left-6 md:left-12 top-1/2 -translate-y-1/2 z-50 flex flex-col gap-8 hidden md:flex">
            {members.map((m, i) => (
               <div key={`p-${i}`} className="flex items-center gap-4">
                  <span className="font-mono text-[10px] text-[#86868b]">{String(i + 2).padStart(2, '0')}</span>
                  <div className="w-[2px] h-12 bg-gray-200 rounded-full overflow-hidden relative">
                     <div
                        ref={el => progressBarsRef.current[i] = el}
                        className="absolute top-0 left-0 w-full bg-black rounded-full"
                        style={{ height: i === 0 ? "100%" : "0%", opacity: i === 0 ? 1 : 0.2 }}
                     />
                  </div>
               </div>
            ))}
         </div>

         <div className="max-w-[90rem] mx-auto w-full h-full relative pl-0 md:pl-24">
            {members.map((member, index) => (
               <div
                  key={member.id}
                  className="absolute inset-0 w-full h-full flex flex-col lg:flex-row items-center justify-between p-6 lg:px-24 pointer-events-none"
               >
                  <div
                     ref={el => leftSidesRef.current[index] = el}
                     className="w-full lg:w-1/2 h-full flex flex-col justify-center pr-0 lg:pr-16 space-y-6 pointer-events-auto"
                  >
                     <div className="anim-text flex items-center gap-4 opacity-0">
                        <span className="font-mono text-xs md:text-sm text-[#86868b] border border-gray-300/50 bg-white/50 px-3 py-1 rounded-md">
                           {member.id}
                        </span>
                        <span className="text-xs md:text-sm font-bold uppercase tracking-widest text-[#1d1d1f]">
                           {member.country || "Global"}
                        </span>
                     </div>

                     <h2 className="anim-text text-5xl md:text-7xl font-bold tracking-tighter text-[#1d1d1f] lg:hidden mb-2 leading-[0.9] opacity-0 pb-2">{member.name}</h2>

                     <div className="anim-text opacity-0">
                        <h3 className="text-3xl md:text-4xl font-medium text-[#1d1d1f] tracking-tight">{member.role}</h3>
                        <p className="text-sm font-mono text-[#86868b] uppercase mt-3 tracking-widest">{member.location}</p>
                     </div>

                     {member.background && (
                        <div className="anim-text opacity-0">
                           <h4 className="text-xs font-bold uppercase tracking-widest text-[#86868b] mb-2">Background</h4>
                           <p className="text-base md:text-lg text-[#1d1d1f]/70 leading-relaxed max-w-xl font-light">
                              {member.background}
                           </p>
                        </div>
                     )}

                     {member.contributions && (
                        <div className="anim-text opacity-0">
                           <h4 className="text-xs font-bold uppercase tracking-widest text-[#86868b] mb-2">Contributions</h4>
                           <p className="text-base md:text-lg text-[#1d1d1f]/70 leading-relaxed max-w-xl font-light">
                              {member.contributions}
                           </p>
                        </div>
                     )}

                     <div className="flex flex-wrap gap-3 mt-6">
                        {member.threeWords?.map((word, i) => (
                           <span key={i} className="anim-tag px-5 py-2.5 bg-white shadow-sm rounded-full text-[11px] md:text-xs font-semibold tracking-wider text-[#1d1d1f] uppercase border border-gray-100 opacity-0">
                              {word}
                           </span>
                        ))}
                     </div>

                     {(member.social?.linkedin || member.social?.email) && (
                        <div className="anim-social flex gap-4 mt-10 pt-8 border-t border-gray-200/50 opacity-0">
                           {member.social?.linkedin && (
                              <a href={`https://linkedin.com/in/${member.social.linkedin}`} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-12 h-12 bg-white border border-gray-200 shadow-sm rounded-full hover:bg-black hover:text-white hover:scale-110 transition-all duration-300 group">
                                 <Linkedin size={20} className="text-current transition-colors" />
                              </a>
                           )}
                           {member.social?.email && (
                              <EmailReveal email={member.social.email} />
                           )}
                        </div>
                     )}
                  </div>

                  <div
                     ref={el => rightSidesRef.current[index] = el}
                     className="w-full lg:w-1/2 h-full flex flex-col items-center justify-center relative mt-8 lg:mt-0 pointer-events-auto"
                  >
                     <div className="anim-img-container relative w-full aspect-[4/5] max-w-md lg:max-w-[32rem] rounded-2xl md:rounded-[2rem] overflow-hidden bg-gray-200 shadow-xl isolate">
                        {member.photo && member.photo !== 'placeholder' ? (
                           <img src={member.photo} alt={member.name} className="anim-img w-full h-full object-cover grayscale opacity-90 mix-blend-multiply transform-origin-bottom" />
                        ) : (
                           <div className="anim-img w-full h-full flex items-center justify-center text-9xl font-bold text-gray-300 select-none bg-white">
                              {member.name.charAt(0)}
                           </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent flex flex-col justify-end p-8 lg:p-14 lg:pb-16 z-10 rounded-2xl md:rounded-[2rem]">
                           <div className="overflow-visible">
                              <h2 className="anim-right-name text-4xl md:text-[4.5rem] font-bold tracking-tighter text-white hidden lg:block leading-tight mb-4">{member.name}</h2>
                           </div>
                        </div>
                     </div>
                  </div>
               </div>
            ))}
         </div>
      </section>
   );
};

// ============================================
// MAIN PAGE
// ============================================
const Team = () => {

   useEffect(() => {
      // Kill everything from Home page before Team initializes
      ScrollTrigger.getAll().forEach(t => t.kill());
      ScrollTrigger.normalizeScroll(false);
      ScrollTrigger.clearScrollMemory();
      window.scrollTo(0, 0);
   }, []);

   return (
      <div className="bg-white min-h-screen font-sans selection:bg-black selection:text-white overflow-x-hidden">
         <TeamHero />
         <MotivationSection />
         <ScrollRoster />
         <MentorsSection />
         <AcknowledgmentsSection />
         <Footer />
      </div>
   );
};

export default Team;
